import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Col, Row } from 'antd'

import B2becInfoLayout from '@/components/B2becInfoLayout/B2becInfoLayout'
import CallToActionButton from '@/components/CallToActionButton'
import ConfirmModal from '@/components/ConfirmModal'
import CustomButton from '@/components/CustomButton'
import { DeleteBorderlessIcon as DeleteIcon, EditIcon } from '@/components/Icons'
import { NotificationManager } from '@/components/Notification'
import EditWishListModal from '@/components/Wishlist/EditWishlistModal'
import { useBreadcrumbs, useDocumentTitle } from '@/hooks'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useDeviceDetect from '@/hooks/useDeviceDetect'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { MODULE_WISHLIST_RIGHTS } from '@/libs/constants/modulerights'

import { useDeleteWishlist } from '../WishlistOverview/useFetchWishlist'
import ProductList from './components/ProductList'
import {
  useAddAllProductsToCart,
  useFetchWishlistDetails,
  useIsSharedWishlist,
  WISHLIST_DETAIL_PAGE_SIZE,
  useRenameWishlist,
} from './useWishlistDetails'
import styles from './WishlistDetail.module.scss'

const WishlistDetailPage = ({ breadcrumbs }) => {
  const { wishlistId, wishlistOwnerId } = useParams()
  const { t } = useTranslation()
  const [isDisplayDeleteModal, setIsDisplayDeleteModal] = useState(false)
  const [shouldDisplayEditModal, setShouldDisplayEditModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const { data: wishlistDetails } = useFetchWishlistDetails({
    wishlistOwnerId,
    wishlistId,
    pageNumber: currentPage,
    pageSize: WISHLIST_DETAIL_PAGE_SIZE,
  })

  const isSharedWishlist = useIsSharedWishlist(
    wishlistOwnerId,
    wishlistId,
    wishlistDetails?.ownerId,
  )

  const wishlistName = wishlistDetails?.wishlistName
  const wishlistOwnerName = wishlistDetails?.ownerName
  const totalProduct = wishlistDetails?.totalProduct ?? 0

  const { mutate: deleteWishlistMutation, isPending: isDeletingWishlist } = useDeleteWishlist({
    shouldNavigate: true,
  })
  const {
    mutate: addAllProductsToCartMutation,
    isPending: isAddingWishlistToCart,
  } = useAddAllProductsToCart()
  const { mutate: renameWishlistMutation } = useRenameWishlist()
  const isLoading = isDeletingWishlist
  const isWishlistEmpty = totalProduct === 0
  const shouldDisableAddToCartButton = isLoading || isWishlistEmpty || isAddingWishlistToCart

  useBreadcrumbs(breadcrumbs)
  useDocumentTitle('pageTitle.wishlistDetail')
  const { isMobile } = useDeviceDetect()

  useAdobeAnalysis(null, {
    pageId: '47',
    pageName: 'wishlist detail page',
    pageType: PAGE_TYPES.WISHLIST,
  })

  const onAddWishlistToCart = useCallback(() => {
    if (wishlistId && wishlistOwnerId) {
      addAllProductsToCartMutation({ wishlistId, wishlistOwnerId })
    }
  }, [wishlistOwnerId, wishlistId, addAllProductsToCartMutation])

  const onDeleteWishList = useCallback(() => {
    if (wishlistId && wishlistOwnerId && !isSharedWishlist) {
      deleteWishlistMutation({
        wishlistId,
        wishlistOwnerId,
      })
    }
  }, [wishlistOwnerId, wishlistId, isSharedWishlist, deleteWishlistMutation])

  const onRenameWishlist = useCallback(
    (newName) => {
      renameWishlistMutation(
        {
          ownerId: wishlistOwnerId,
          wishlistId,
          newName,
        },
        {
          onSuccess: () => {
            NotificationManager.success({
              message: t('notification.success.editWishlist'),
            })
          },
        },
      )
    },
    [renameWishlistMutation, wishlistId, wishlistOwnerId, t],
  )

  return (
    <div>
      <B2becInfoLayout>
        <B2becInfoLayout.Title title={wishlistName}>
          <Row justify="space-between">
            <Col flex="auto">
              <p>
                {t('wishlist.details.totalProduct', {
                  count: totalProduct ?? 0,
                })}
              </p>
              <p className={styles['owner-name']}>{wishlistOwnerName}</p>
            </Col>
            <Col>
              <div className={styles['title-content-wrapper']}>
                <div className={styles['wishlist-actions-container']}>
                  {!isSharedWishlist && (
                    <PermissionWrapper
                      permission={MODULE_WISHLIST_RIGHTS.DELETE_WISHLIST}
                    >
                      <CallToActionButton
                        leftIcon={<DeleteIcon />}
                        className={styles['actions-btn']}
                        onClick={() => setIsDisplayDeleteModal(true)}
                        disabled={isLoading}
                      />
                    </PermissionWrapper>
                  )}
                  {!isSharedWishlist && (
                    <PermissionWrapper
                      permission={MODULE_WISHLIST_RIGHTS.EDIT_SHARE_WISHLIST}
                    >
                      <CallToActionButton
                        leftIcon={<EditIcon />}
                        className={styles['actions-btn']}
                        onClick={() => {
                          setShouldDisplayEditModal(true)
                        }}
                        disabled={isLoading}
                      />
                    </PermissionWrapper>
                  )}
                  <PermissionWrapper
                    permission={MODULE_WISHLIST_RIGHTS.ADD_WISHLIST_TO_CART}
                  >
                    {isMobile ? null : (
                      <CustomButton
                        onClick={onAddWishlistToCart}
                        className={styles['header-buttons']}
                        disabled={shouldDisableAddToCartButton}
                      >
                        {t('buttonTexts.addToCart')}
                      </CustomButton>
                    )}
                  </PermissionWrapper>
                </div>
              </div>
            </Col>
          </Row>
        </B2becInfoLayout.Title>
        <B2becInfoLayout.Content className={styles['layout-content-wrapper']}>
          <ProductList
            wishlistId={wishlistId}
            wishlistOwnerId={wishlistOwnerId}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        </B2becInfoLayout.Content>
      </B2becInfoLayout>

      {isMobile
        ? (
          <div className={styles['mobile-actions-section']}>
            <div className={styles['buttons-wrapper']}>
              <CustomButton
                onClick={onAddWishlistToCart}
                className={styles['header-buttons']}
                disabled={shouldDisableAddToCartButton}
              >
                {t('buttonTexts.shopAll')}
              </CustomButton>
            </div>
          </div>
        )
        : null}

      <ConfirmModal
        confirmActionHandler={onDeleteWishList}
        isShowModal={isDisplayDeleteModal && !isSharedWishlist}
        modalContent={t('wishlist.modal.deleteWishlist.modalContent')}
        modalTitle={t('wishlist.modal.deleteWishlist.title')}
        setIsShowModal={setIsDisplayDeleteModal}
      />

      {shouldDisplayEditModal && !isSharedWishlist && (
        <EditWishListModal
          wishlistId={wishlistId}
          wishlistName={wishlistName}
          ownerId={wishlistOwnerId}
          shouldShowModal={shouldDisplayEditModal}
          setShouldShowModal={setShouldDisplayEditModal}
          shouldShowSharing
          onRenameWishlist={onRenameWishlist}
        />
      )}
    </div>
  )
}

export default WishlistDetailPage
